import { useQuery } from '@tanstack/react-query';
import { Link, Redirect } from 'wouter';
import { useAuth } from '@/contexts/auth-context';

interface WebhookHealth {
  configured: boolean;
  lastReceivedAt: string | null;
  lastEvent: string | null;
  secondsSinceLastReceived: number | null;
}

interface SessionListItem {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  projectId: string | null;
  participantCount: number;
  segmentCount: number;
  totalCostCents: number;
}

export default function AdminTranscriptsListPage() {
  const { user, isLoading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery<{ sessions: SessionListItem[] }>({
    queryKey: ['/api/calls/_internal/recent-with-transcripts'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: webhookHealth } = useQuery<WebhookHealth>({
    queryKey: ['/api/calls/_internal/livekit-webhook-status'],
    enabled: !!user && user.role === 'admin',
    refetchInterval: 30_000,
  });

  if (authLoading) {
    return <div className="p-6 font-mono text-sm text-gray-300">Loading…</div>;
  }
  if (!user || user.role !== 'admin') {
    return <Redirect to="/" />;
  }

  return (
    <div className="min-h-screen bg-black text-gray-200 p-6 font-mono text-sm">
      <h1 className="text-lg font-bold mb-4">
        Transcript Viewer (dev/admin) — recent sessions with transcripts
      </h1>
      <p className="mb-4 text-gray-400">
        Only sessions you were a participant in are listed. Click a session to inspect raw chunks.
      </p>
      {webhookHealth && (() => {
        const secs = webhookHealth.secondsSinceLastReceived;
        const isHealthy = secs !== null && secs < 24 * 60 * 60;
        const isDisabled = !webhookHealth.configured;
        const bgClass = isDisabled
          ? 'bg-gray-900 border-gray-700 text-gray-400'
          : isHealthy
            ? 'bg-green-950 border-green-800 text-green-200'
            : 'bg-amber-950 border-amber-800 text-amber-200';
        const label = isDisabled
          ? 'LiveKit not configured'
          : secs === null
            ? 'NEVER — no webhook has been received since server boot'
            : isHealthy
              ? `OK — last webhook ${secs}s ago (${webhookHealth.lastEvent ?? 'unknown event'})`
              : `STALE — last webhook ${Math.round(secs / 3600)}h ago (${webhookHealth.lastEvent ?? 'unknown event'})`;
        return (
          <div
            className={`mb-4 border px-3 py-2 ${bgClass}`}
            data-testid="livekit-webhook-health-card"
          >
            <div className="font-bold mb-1">Webhook delivery health</div>
            <div>{label}</div>
            {webhookHealth.lastReceivedAt && (
              <div className="text-xs opacity-70 mt-1">at {webhookHealth.lastReceivedAt}</div>
            )}
            {secs === null && webhookHealth.configured && (
              <div className="text-xs opacity-80 mt-1">
                If this persists, check the LiveKit Cloud dashboard webhook URL points at this host's
                {' '}<code>/api/calls/livekit/webhook</code>.
              </div>
            )}
          </div>
        );
      })()}
      {isLoading && <div>Loading…</div>}
      {error && (
        <div className="text-red-400" data-testid="transcripts-list-error">
          Error: {(error as Error).message}
        </div>
      )}
      {data && data.sessions.length === 0 && (
        <div className="text-gray-500" data-testid="transcripts-list-empty">
          No sessions found.
        </div>
      )}
      {data && data.sessions.length > 0 && (
        <table className="w-full border-collapse" data-testid="transcripts-list-table">
          <thead>
            <tr className="border-b border-gray-700 text-left">
              <th className="py-1 pr-4">sessionId</th>
              <th className="py-1 pr-4">startedAt</th>
              <th className="py-1 pr-4">endedAt</th>
              <th className="py-1 pr-4">status</th>
              <th className="py-1 pr-4">projectId</th>
              <th className="py-1 pr-4">participants</th>
              <th className="py-1 pr-4">segments</th>
              <th className="py-1 pr-4">costCents</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.map(s => (
              <tr key={s.sessionId} className="border-b border-gray-800 hover:bg-gray-900">
                <td className="py-1 pr-4">
                  <Link
                    href={`/admin/transcripts/${s.sessionId}`}
                    className="text-blue-400 underline"
                    data-testid={`transcript-link-${s.sessionId}`}
                  >
                    {s.sessionId}
                  </Link>
                </td>
                <td className="py-1 pr-4">{s.startedAt}</td>
                <td className="py-1 pr-4">{s.endedAt ?? '—'}</td>
                <td className="py-1 pr-4">{s.status}</td>
                <td className="py-1 pr-4">{s.projectId ?? '—'}</td>
                <td className="py-1 pr-4">{s.participantCount}</td>
                <td className="py-1 pr-4">{s.segmentCount}</td>
                <td className="py-1 pr-4">{s.totalCostCents}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
